import { apiFetch } from './http';

// ── Types ──

export type StepType = 'action' | 'checklist' | 'document' | 'deploy' | 'wait' | 'review' | 'call';
export type WorkflowStatus = 'not_started' | 'in_progress' | 'blocked' | 'stalled' | 'completed' | 'cancelled';
export type StepStatus = 'pending' | 'in_progress' | 'completed' | 'skipped' | 'blocked';

export interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  required: boolean;
}

export interface DocumentRequirement {
  id: string;
  label: string;
  description: string;
  required: boolean;
}

export interface StepDefinition {
  index: number;
  title: string;
  description: string;
  type: StepType;
  instructions?: string[];
  checklist?: ChecklistItem[];
  documents?: DocumentRequirement[];
  waitHint?: string;
  callConfig?: { defaultTime?: string; contactEmail?: string; meetingLink?: string };
  estimatedMinutes?: number;
  stallThresholdMinutes?: number;
}

export interface StepProgress {
  stepIndex: number;
  status: StepStatus;
  startedAt?: string;
  completedAt?: string;
  checklistProgress?: Record<string, boolean>;
  documentLinks?: Record<string, string>;
  notes?: string;
  data?: Record<string, unknown>;
}

export interface WorkflowTemplate {
  templateId: string;
  name: string;
  description: string;
  productId: string;
  applicableTiers: string[];
  version: string;
  steps: StepDefinition[];
}

export interface WorkflowInstance {
  workflowId: string;
  tenantId: string;
  templateId: string;
  templateName: string;
  productId: string;
  status: WorkflowStatus;
  currentStep: number;
  totalSteps: number;
  steps: StepDefinition[];
  progress: StepProgress[];
  assignedTo?: string;
  customerContact?: { name: string; email: string };
  scheduledCall?: { dateTime: string; timezone: string; meetingLink?: string };
  stallDetectedAt?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface DashboardWorkflow {
  workflowId: string;
  tenantId: string;
  templateName: string;
  productId: string;
  status: WorkflowStatus | 'stalled';
  currentStep: number;
  currentStepTitle: string;
  totalSteps: number;
  completedSteps: number;
  percentComplete: number;
  assignedTo?: string;
  customerContact?: { name: string; email: string };
  scheduledCall?: { dateTime: string; timezone: string };
  isStalled: boolean;
  stallDuration: number | null;
  lastActivity: string;
  createdAt: string;
  completedAt?: string;
}

export interface DashboardSummary {
  total: number;
  inProgress: number;
  stalled: number;
  completed: number;
  blocked: number;
}

// ── API Client ──


// Templates

export async function fetchTemplates(): Promise<{ templates: WorkflowTemplate[] }> {
  return apiFetch('/ops/onboarding/templates');
}

export async function fetchTemplate(templateId: string): Promise<WorkflowTemplate> {
  return apiFetch(`/ops/onboarding/templates/${templateId}`);
}

export async function createTemplate(template: Omit<WorkflowTemplate, 'version'>): Promise<WorkflowTemplate> {
  return apiFetch('/ops/onboarding/templates', {
    method: 'POST',
    body: JSON.stringify(template),
  });
}

// Workflows

export async function fetchWorkflows(): Promise<{ workflows: WorkflowInstance[] }> {
  return apiFetch('/ops/onboarding/workflows');
}

export async function createWorkflow(input: {
  tenantId: string;
  templateId: string;
  assignedTo?: string;
  customerContact?: { name: string; email: string };
  scheduledCall?: { dateTime: string; timezone: string; meetingLink?: string };
}): Promise<WorkflowInstance> {
  return apiFetch('/ops/onboarding/workflows', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function fetchWorkflow(workflowId: string, tenantId: string): Promise<WorkflowInstance> {
  return apiFetch(`/ops/onboarding/workflows/${workflowId}?tenantId=${tenantId}`);
}

export async function updateWorkflow(
  workflowId: string,
  tenantId: string,
  updates: Partial<Pick<WorkflowInstance, 'status' | 'assignedTo' | 'scheduledCall' | 'customerContact'>>
): Promise<unknown> {
  return apiFetch(`/ops/onboarding/workflows/${workflowId}?tenantId=${tenantId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// Steps

export async function updateStep(
  workflowId: string,
  tenantId: string,
  stepUpdate: {
    stepIndex: number;
    status?: StepStatus;
    checklistProgress?: Record<string, boolean>;
    documentLinks?: Record<string, string>;
    notes?: string;
  }
): Promise<{
  workflowId: string;
  stepIndex: number;
  stepStatus: StepStatus;
  workflowStatus: WorkflowStatus;
  currentStep: number;
  progress: StepProgress[];
}> {
  return apiFetch(`/ops/onboarding/workflows/${workflowId}/steps/${stepUpdate.stepIndex}?tenantId=${tenantId}`, {
    method: 'PUT',
    body: JSON.stringify(stepUpdate),
  });
}

// Dashboard

export async function fetchDashboard(): Promise<{ summary: DashboardSummary; workflows: DashboardWorkflow[] }> {
  return apiFetch('/ops/onboarding/dashboard');
}
